import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { baseService } from './base.service'
import { CreateHallOfFameProjectRequest, UpdateHallOfFameProjectRequest } from './dto/hall-of-fame-project.dto'
import { IBaseService } from './interfaces/base-service.interface'

import { QueryParams } from '@/types/base.type'
import { HallOfFameProject } from '@/types/hall-of-fame-project.type'
import { ListResponse } from '@/types/list.type'

class HallOfFameProjectService {
  private static instance: HallOfFameProjectService
  private readonly api: AxiosInstance

  constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  private getQueryParams(locale?: string) {
    return locale ? `?locale=${locale}` : ''
  }

  public static getInstance(baseService: IBaseService): HallOfFameProjectService {
    if (!HallOfFameProjectService.instance) {
      HallOfFameProjectService.instance = new HallOfFameProjectService(baseService)
    }
    return HallOfFameProjectService.instance
  }

  async getProjects({
    queryParams,
    locale,
    options,
  }: {
    queryParams: QueryParams
    locale?: string
    options?: AxiosRequestConfig
  }): Promise<ListResponse<HallOfFameProject>> {
    const { data } = await this.api.get('/hall-of-fame', { params: { ...queryParams, locale }, ...options })
    return data
  }

  async getProjectById(id: string, locale?: string, options?: AxiosRequestConfig): Promise<HallOfFameProject> {
    const { data } = await this.api.get(`/hall-of-fame/${id}${this.getQueryParams(locale)}`, options)
    return data
  }

  async createProject(
    project: CreateHallOfFameProjectRequest,
    locale?: string,
    options?: AxiosRequestConfig
  ): Promise<HallOfFameProject> {
    const { data } = await this.api.post(`/hall-of-fame${this.getQueryParams(locale)}`, project, options)
    return data
  }

  async updateProject(
    id: string,
    project: UpdateHallOfFameProjectRequest,
    locale?: string,
    options?: AxiosRequestConfig
  ): Promise<HallOfFameProject> {
    const { data } = await this.api.put(`/hall-of-fame/${id}${this.getQueryParams(locale)}`, project, options)
    return data
  }

  async deleteProject(id: string, options?: AxiosRequestConfig) {
    return await this.api.delete(`/hall-of-fame/${id}`, options)
  }

  async updateAllProjectStars(options?: AxiosRequestConfig) {
    return await this.api.post('/hall-of-fame/update-all-stars', {}, options)
  }

  async assignUserProject(id: string, userId: number, options?: AxiosRequestConfig) {
    return await this.api.post(`/hall-of-fame/${id}/assign-user`, { userId }, options)
  }

  async getProjectUserTopic(userId: number, options?: AxiosRequestConfig): Promise<ListResponse<HallOfFameProject>> {
    const { data } = await this.api.get<ListResponse<HallOfFameProject>>(
      `/hall-of-fame/${userId}/project-topic`,
      options
    )
    return data
  }
}

const hallOfFameProjectService = HallOfFameProjectService.getInstance(baseService)

export default hallOfFameProjectService
